import React, { Fragment, useState, useEffect, useMemo } from "react"
import { Box, CircularProgress } from "@material-ui/core"
import { withRouter } from "react-router"
import { MuiThemeProvider } from "@material-ui/core/styles"
import billingTheme from "../../themes/billing"
import { useDispatch, useSelector } from "react-redux"
import { isEmpty } from "lodash"
import { getClientDealMemo } from "../../actions/billing-crew-client"
import InvoiceCardBillingClient from "../../components/Billing/Client/InvoiceCardBillingClient/index"
import BillingPageLayout from "../../components/Billing/Common/BillingPageLayout/index"
import DownloadPdfButton from "../../components/Billing/Common/DownloadPdfButton/index"
import CrewmamaAppBar from "../../components/CrewmamaAppBar/index"
import ClientInvoiceRightSide from "../../components/Billing/Client/ClientInvoiceRightSide/index"
import calculateTotals from "../../utils/calculateTotals"
import useActiveCompanyAdesk from "../../hooks/useActiveCompanyAdesk"
import convertAdeskToOrganization from "../../utils/convertAdeskToOrganization"
import { adeskPhones } from "../../utils/adeskPhones"
import { Button } from "@material-ui/core"
import LinkIcon from "@material-ui/icons/Link"
import copy from "copy-to-clipboard"

const ClientInvoiceContent = props => {
  const dispatch = useDispatch()
  let shortCode = props.match.params.short_code

  //master local state for all client invoice components
  const [localInvoiceData, setLocalInvoiceData] = useState({})
  const [usersRolesArray, setUsersRolesArray] = useState({})

  // Make call to populate the state val being used in the tables, etc.
  // Combining users & roles HERE
  useEffect(() => {
    getInvoiceForClient()
  }, [])

  const reCallInvoice = useSelector(state => state.billing.recallInvoice)

  const getInvoiceForClient = () => {
    dispatch(getClientDealMemo(shortCode))
      .then(res => {
        // create two separate states -
        // 1) usersRolesArray - combined all users/roles into each array, with each's expenses & additional consolidated into expenses
        setUsersRolesArray(() => {
          if (res.users || res.roles) {
            return [
              ...res.users.map(user => {
                let newAdditionalItems = user.additional_expenses
                delete user.additional_expenses
                return {
                  ...user,
                  expenses: [...user.expenses, ...newAdditionalItems],
                }
              }),
              ...res.roles.map(role => {
                let newAdditionalItems = role.additional_expenses
                delete role.additional_expenses
                return {
                  ...role,
                  expenses: [...role.expenses, ...newAdditionalItems],
                }
              }),
            ].sort((a, b) => a.shoot_role_id - b.shoot_role_id)
          } else {
            return []
          }
        })
        delete res.users
        delete res.roles
        // 2) localInvoiceData akin to general data - rest of stuff without users, roles
        setLocalInvoiceData(res)
      })
      .catch(err => {
        console.log(err)
      })
  }

  useEffect(
    () => {
      if (reCallInvoice === true) {
        getInvoiceForClient()
      }
    },
    [reCallInvoice]
  )

  const paidAmt = useMemo(
    () => {
      let totalPaid
      let depositRequired = Boolean(localInvoiceData.deposit_required)
      //if depositRequired
      let firstPaymentPaid = Boolean(localInvoiceData.first_paid)
      let secondPaymentPaid = Boolean(localInvoiceData.second_paid)
      //if not depositRequired
      let fullPaymentPaid = Boolean(Boolean(localInvoiceData.full_paid))

      if (depositRequired) {
        if (firstPaymentPaid) {
          if (secondPaymentPaid) {
            totalPaid =
              localInvoiceData.first_half_amount +
              localInvoiceData.second_half_amount
          } else {
            totalPaid = localInvoiceData.first_half_amount
          }
        } else {
          if (secondPaymentPaid) {
            totalPaid = localInvoiceData.second_half_amount
          } else {
            totalPaid = 0
          }
        }
      } else {
        if (fullPaymentPaid) {
          totalPaid = localInvoiceData.full_amount
        } else {
          totalPaid = 0
        }
      }
      return totalPaid
    },
    [localInvoiceData]
  )

  const amounts = useMemo(
    () => {
      if (usersRolesArray && !isEmpty(localInvoiceData)) {
        return calculateTotals(
          usersRolesArray,
          paidAmt,
          localInvoiceData.discount
        )
      } else {
        return {}
      }
    },
    [usersRolesArray, paidAmt, localInvoiceData]
  )

  const checkDiscount = useMemo(
    () => {
      if (usersRolesArray.length > 0) {
        let discountArr = []
        // usersrolesarray combines both reg & additional expenses for all roles & users
        usersRolesArray.map(x =>
          x.expenses.filter(y => {
            if (y.discount && y.discount > 0) {
              discountArr = [...discountArr, y]
            }
            return discountArr
          })
        )
        return discountArr.length > 0
      } else {
        return false
      }
    },
    [usersRolesArray]
  )

  const copyLink = () => {
    copy(window.location.href)
  }

  const isAdesk =
    useActiveCompanyAdesk() ||
    (localInvoiceData && localInvoiceData.company_id === 2)

  if (isEmpty(localInvoiceData)) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 64px)",
        }}
      >
        <CircularProgress />
      </div>
    )
  } else {
    return (
      <BillingPageLayout
        leftHeader={`Invoice for ${localInvoiceData.shoot_name}`}
        leftContent={
          <InvoiceCardBillingClient
            generalData={localInvoiceData}
            usersRolesArray={usersRolesArray}
            setUsersRolesArray={setUsersRolesArray}
            passedAmounts={amounts}
            checkDiscount={checkDiscount}
            {...props}
          />
        }
        rightHeader="Actions"
        rightHeaderButtons={
          <Fragment>
            <DownloadPdfButton
              type="invoice"
              data={{
                ...localInvoiceData,
                users: usersRolesArray,
                passedAmounts: amounts,
                company_name:
                  isAdesk &&
                  (localInvoiceData && localInvoiceData.crewmama_or_gtt)
                    ? convertAdeskToOrganization(
                        localInvoiceData.crewmama_or_gtt
                      )
                    : localInvoiceData.company_name,
                company_phone: isAdesk
                  ? adeskPhones(localInvoiceData.crewmama_or_gtt)
                  : localInvoiceData.company_phone,
                hasLineDiscounts: checkDiscount,
              }}
              client={true}
            />

            <Box width="10px" />
            {/* <CopyLinkButton docType="invoice" {...props} /> */}

            <Button
              onClick={copyLink}
              size="small"
              variant="outlined"
              style={{
                border: "0px solid green",
                borderRadius: "5px",
                backgroundColor: "#EBEBEB",
              }}
            >
              <LinkIcon />
              Copy Link
            </Button>
          </Fragment>
        }
        rightContent={
          <ClientInvoiceRightSide
            generalData={localInvoiceData}
            setGeneralData={setLocalInvoiceData}
            usersRolesArray={usersRolesArray}
            passedAmounts={amounts}
            reCallInvoiceForClient={() => {}}
          />
        }
      />
    )
  }
}

const ClientInvoicePage = props => {
  return (
    <MuiThemeProvider theme={billingTheme}>
      <CrewmamaAppBar />
      <ClientInvoiceContent {...props} />
    </MuiThemeProvider>
  )
}

export default withRouter(ClientInvoicePage)
